import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import FormLivePreview from './FormLivePreview';
import {
  getStoreForms,
  getStoreFormItems,
  getStoreFormTypes,
  installStoreForm,
} from '../api/storeFormsAPI';

const STORE_INITIAL_LIMIT = 25;
const STORE_SCROLL_LIMIT = 10;

const FormStoreModal = ({ isOpen, onClose, onInstallSuccess, renderPreviewInput }) => {
  const storeListRef = useRef(null);
  const hasLoadedOnceRef = useRef(false);
  const [storeForms, setStoreForms] = useState([]);
  const [storeFormTypes, setStoreFormTypes] = useState([]);
  const [storeSearchTerm, setStoreSearchTerm] = useState('');
  const [storeTypeFilter, setStoreTypeFilter] = useState('all');
  const [storeHasMore, setStoreHasMore] = useState(true);
  const [storeLoading, setStoreLoading] = useState(false);
  const [storePreviewForm, setStorePreviewForm] = useState(null);
  const [storePreviewItems, setStorePreviewItems] = useState([]);
  const [storePreviewLoading, setStorePreviewLoading] = useState(false);
  const [installCandidate, setInstallCandidate] = useState(null);
  const [installProgress, setInstallProgress] = useState(0);
  const [installError, setInstallError] = useState('');

  const resetStoreState = useCallback(() => {
    hasLoadedOnceRef.current = false;
    setStoreForms([]);
    setStoreFormTypes([]);
    setStoreSearchTerm('');
    setStoreTypeFilter('all');
    setStoreHasMore(true);
    setStoreLoading(false);
    setStorePreviewForm(null);
    setStorePreviewItems([]);
    setStorePreviewLoading(false);
    setInstallCandidate(null);
    setInstallProgress(0);
    setInstallError('');
  }, []);

  const loadStoreFormTypes = useCallback(async () => {
    try {
      const data = await getStoreFormTypes();
      setStoreFormTypes(data);
    } catch (err) {
      console.error('Error fetching store form types:', err);
    }
  }, []);

  const loadStoreForms = useCallback(
    async (reset = false) => {
      if (storeLoading) return;
      setStoreLoading(true);
      try {
        const skip = reset ? 0 : storeForms.length;
        const take = reset ? STORE_INITIAL_LIMIT : STORE_SCROLL_LIMIT;
        const response = await getStoreForms({
          search: storeSearchTerm || undefined,
          formTypeId: storeTypeFilter !== 'all' ? storeTypeFilter : undefined,
          skip,
          take,
        });
        setStoreForms((prev) => {
          const base = reset ? [] : prev;
          const next = [...base, ...(response?.data || [])];
          const total = response?.total ?? next.length;
          setStoreHasMore(next.length < total);
          return next;
        });
      } catch (err) {
        console.error('Error fetching store forms:', err);
      } finally {
        setStoreLoading(false);
      }
    },
    [storeForms.length, storeLoading, storeSearchTerm, storeTypeFilter],
  );

  const openStorePreview = async (form) => {
    setStorePreviewForm(form);
    setStorePreviewItems([]);
    setStorePreviewLoading(true);
    try {
      const items = await getStoreFormItems(form.id);
      setStorePreviewItems(items || []);
    } catch (err) {
      console.error('Error fetching store form items:', err);
      setStorePreviewItems([]);
    } finally {
      setStorePreviewLoading(false);
    }
  };

  const handleInstallStart = (form) => {
    setInstallCandidate(form);
    setInstallProgress(0);
    setInstallError('');
  };

  const handleInstallConfirm = async () => {
    if (!installCandidate) return;
    setInstallError('');
    setInstallProgress(25);
    try {
      setInstallProgress(60);
      await installStoreForm(installCandidate.id);
      setInstallProgress(100);
      if (onInstallSuccess) {
        await onInstallSuccess();
      }
      setTimeout(() => {
        setInstallCandidate(null);
        setInstallProgress(0);
      }, 300);
    } catch (err) {
      console.error('Error installing store form:', err);
      setInstallError('Unable to install form. Please try again.');
      setInstallProgress(0);
    }
  };

  const handleCloseModal = () => {
    resetStoreState();
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      resetStoreState();
      return;
    }
    resetStoreState();
  }, [isOpen, resetStoreState]);

  useEffect(() => {
    if (!isOpen) return;
    loadStoreFormTypes();
    loadStoreForms(true);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!hasLoadedOnceRef.current) {
      hasLoadedOnceRef.current = true;
      return;
    }
    const debounce = setTimeout(() => loadStoreForms(true), 350);
    return () => clearTimeout(debounce);
  }, [isOpen, storeSearchTerm, storeTypeFilter]);

  useEffect(() => {
    if (!isOpen) return;
    const container = storeListRef.current;
    if (!container) return;

    const onScroll = () => {
      if (
        container.scrollTop + container.clientHeight >= container.scrollHeight - 5 &&
        storeHasMore &&
        !storeLoading
      ) {
        loadStoreForms(false);
      }
    };

    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [isOpen, loadStoreForms, storeHasMore, storeLoading]);

  return (
    <>
      {isOpen && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-xxl modal-dialog-scrollable">
            <div className="modal-content border-0">
              <div className="modal-header border-0 pb-0">
                <img
                  src="/assets/images/logo.png"
                  alt="Samurai logo"
                  style={{ height: "45px" }}
                />
                <button type="button" className="btn-close" onClick={handleCloseModal} />
              </div>
              <div
                className="modal-body p-0"
                ref={storeListRef}
                style={{ overflowY: 'auto' }}
              >
                <div className="pt-12 px-4 pb-4">
                  <div
                    className="overflow-hidden mb-4 position-relative"
                    style={{
                      minHeight: '300px',
                      backgroundImage: "url('/assets/images/store-header.png')",
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  >
                    <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark opacity-50" />

                    <div
                      className="position-absolute top-50 translate-middle text-start text-white px-3"
                      style={{ zIndex: 2, left: "35%" }}
                    >
                      <h2 className="fw-bold mb-2 text-white">
                        Forms, Inspections and Procedures Store
                      </h2>
                      <p className="mb-0 text-xl">
                        Browse and install ready to use forms
                      </p>
                    </div>
                  </div>

                  <div className="row gx-4 mb-4 mx-24">
                    <div className="col-12 col-lg-3">
                      <div className="d-flex flex-wrap gap-3 mb-4">
                        <div
                          className="input-group mx-auto mt-12"
                          style={{ maxWidth: "380px" }}
                        >
                          <span className="input-group-text bg-white text-primary border-primary">
                            <Icon icon="mage:search" />
                          </span>
                          <input
                            type="search"
                            className="form-control border-primary py-24"
                            placeholder="Search forms and inspections"
                            value={storeSearchTerm}
                            onChange={(e) => setStoreSearchTerm(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="d-flex flex-column gap-2">
                        <button
                          type="button"
                          className={`btn d-flex align-items-center gap-2 px-16 py-16 w-100 justify-content-start text-md rounded-0
                            ${storeTypeFilter === 'all'
                              ? 'text-primary bg-primary-focus'
                              : 'btn-none'
                            }`}
                          style={{
                            borderLeftWidth: '4px',
                            borderLeftStyle: 'solid',
                            borderLeftColor:
                              storeTypeFilter === 'all' ? 'var(--bs-primary)' : 'transparent',
                          }}
                          onClick={() => setStoreTypeFilter('all')}
                        >
                          <Icon icon="mdi:folder-multiple-outline" className="icon text-lg" />
                          All Forms
                        </button>
                        {storeFormTypes.map((type) => (
                          <button
                            key={type.id}
                            type="button"
                            className={`btn d-flex align-items-center gap-2 px-16 py-16 w-100 justify-content-start text-md rounded-0
                              ${Number(storeTypeFilter) === Number(type.id)
                                ? 'text-primary bg-primary-focus'
                                : 'btn-none'
                              }`}
                            style={{
                              borderLeftWidth: '4px',
                              borderLeftStyle: 'solid',
                              borderLeftColor:
                                Number(storeTypeFilter) === Number(type.id) ? 'var(--bs-primary)' : 'transparent'
                            }}
                            onClick={() => setStoreTypeFilter(type.id)}
                          >

                            <Icon icon={type.icon || 'mdi:file-document-outline'} className="icon text-lg" />
                            {type.name}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="col-12 col-lg-9">
                      <div style={{ height: "calc(100vh - 500px)", overflowY: "auto", overflowX: "hidden" }}>
                        <div className="row g-3 mb-24">
                          {storeForms.length === 0 && !storeLoading && (
                            <div className="text-center text-neutral-500 py-4">No forms found in store.</div>
                          )}
                          {storeForms.map((form) => {
                            const imageUrl = form.image
                              ? `${process.env.REACT_APP_SPACES_BASE}/${form.image}`
                              : `${process.env.REACT_APP_SPACES_BASE}/placeholder.png`;
                            return (
                              <div key={form.id} className="col-lg-4 col-md-4">
                                <div className="card h-100 p-0 rounded-0 overflow-hidden shadow-sm">
                                  <div className="card-body d-flex flex-column">
                                    <div className="w-100 max-h-194-px radius-8 overflow-hidden">
                                      <img
                                        src={imageUrl}
                                        alt={form.name}
                                        className="w-100 h-100 object-fit-cover"
                                      />
                                    </div>
                                    <div className="mt-20 d-flex flex-column flex-grow-1">
                                      {/*<div className="d-flex align-items-center gap-6 justify-content-between flex-wrap mb-16">
                                        <span className="px-20 py-6 bg-neutral-100 rounded-pill bg-hover-neutral-300 text-neutral-600 fw-medium">
                                          {form.formType?.name || 'Uncategorised'}
                                        </span>
                                        <div className="d-flex align-items-center gap-8 text-neutral-500 fw-medium">
                                          <i className="ri-calendar-2-line" />
                                          {new Date(form.createdAt).toLocaleDateString()}
                                        </div>
                                      </div>*/}
                                      <h6 className="mb-12">
                                        <span className="text-line-2 text-hover-primary-600 text-xl transition-2">
                                          {form.name}
                                        </span>
                                      </h6>
                                      <p className="text-line-2 text-neutral-500 flex-grow-1">
                                        {form.description}
                                      </p>
                                      <p>
                                        <span className="text-black fw-semibold ">$0.00</span> - <span className="text-sm text-alert">Free with current plan</span>
                                      </p>
                                      <div className="d-flex gap-2 mt-2">
                                        <button
                                          className="btn btn-outline-secondary btn-sm text-md d-flex align-items-center justify-content-center gap-2 flex-grow-1" style={{ width: "40%" }}
                                          onClick={() => openStorePreview(form)}
                                        >
                                          <Icon icon="mdi:eye-outline" className="icon text-xl" />
                                          <span style={{ position: "relative", top: "1px" }}>Preview</span>
                                        </button>
                                        <button
                                          className="btn btn-primary btn-sm text-md d-flex align-items-center justify-content-center gap-2 flex-grow-1" style={{ width: "40%" }}
                                          onClick={() => handleInstallStart(form)}
                                        >
                                          <Icon icon="mdi:arrow-collapse-down" className="icon text-xl" />
                                          <span style={{ position: "relative", top: "1px" }}>Install</span>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {storeLoading && (
                            <div className="text-center py-3 w-100">Loading forms...</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {storePreviewForm && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-body d-flex justify-content-center p-0 pt-0" style={{ width: "650px" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ position: "absolute", right: "15px", top: "15px" }}
                  onClick={() => {
                    setStorePreviewForm(null);
                    setStorePreviewItems([]);
                    setStorePreviewLoading(false);
                  }}
                />
                {storePreviewLoading ? (
                  <div className="py-4">Loading preview...</div>
                ) : (
                  <FormLivePreview
                    form={storePreviewForm}
                    items={storePreviewItems}
                    renderPreviewInput={renderPreviewInput}
                    onClose={() => {
                      setStorePreviewForm(null);
                      setStorePreviewItems([]);
                      setStorePreviewLoading(false);
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {installCandidate && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Install this form to your database?</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setInstallCandidate(null);
                    setInstallProgress(0);
                    setInstallError('');
                  }}
                />
              </div>
              <div className="modal-body">
                <div className="d-flex gap-3 align-items-start">
                  <div className="flex-shrink-0">
                    <img
                      src={
                        installCandidate.image
                          ? `${process.env.REACT_APP_SPACES_BASE}/${installCandidate.image}`
                          : `${process.env.REACT_APP_SPACES_BASE}/placeholder.png`
                      }
                      alt={installCandidate.name}
                      className="rounded-3"
                      style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                    />
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="mb-8 text-lg">{installCandidate.name}</h6>
                    <p className="text-muted text-sm mb-0">{installCandidate.description}</p>
                    <p className="mt-8 mb-0"><span className="text-black fw-semibold">$0.00</span> - <span className="text-sm text-alert">Free with current plan</span></p>
                  </div>
                </div>
                {installError && <div className="alert alert-danger">{installError}</div>}
                {installProgress > 0 && (
                  <div className="progress mt-16" role="progressbar" aria-valuenow={installProgress} aria-valuemin="0" aria-valuemax="100">
                    <div
                      className="progress-bar bg-primary"
                      style={{ width: `${installProgress}%` }}
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setInstallCandidate(null);
                    setInstallProgress(0);
                    setInstallError('');
                  }}
                  disabled={installProgress > 0 && installProgress < 100}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleInstallConfirm}
                  disabled={installProgress > 0 && installProgress < 100}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FormStoreModal;
